import React, { useEffect, useState, type FC } from 'react';
import classnames from 'classnames';
import { CategoryID } from '@sbt-web/network/types';
import type { Categories } from '@sbt-web/category-utils';
import {
  AllCategoriesModal,
  ArrowDown,
  Button,
  Category as CategoryIcon,
} from '@sbt-web/ui';
import { getOrCreatePulse } from '@tools/tracking/utils';
import { useAppSelector } from '@reducers/common';
import { countFilters } from './count_filters';

import classes from './category-selector.module.scss';

const filtersButtonClickEvent = {
  type: 'Click',
  object: {
    type: 'UIElement',
    '@id': 'sdrn:subito:listing:search:element:filters-button',
    name: 'Mobile Filters Button',
    elementType: 'Button',
  },
};

type Props = {
  categoryList: Categories;
  categoryIndex: number;
  onCategoryChange: (opts: CategoryInfo) => void;
  onFiltersButtonClick: React.EventHandler<React.MouseEvent>;
};

type CategoryInfo = {
  key: CategoryID;
  label: string;
};

export const CategorySelector: FC<Props> = ({
  categoryList,
  categoryIndex,
  onCategoryChange,
  onFiltersButtonClick,
}) => {
  const filterCount = useAppSelector(({ search }) => {
    let customFilterCount = 0;

    if (search.qso) {
      customFilterCount += 1;
    }

    if (search.includeShippableOnly) {
      customFilterCount += 1;
    }

    if (search.includeUrgent) {
      customFilterCount += 1;
    }

    return countFilters(search.filters) + customFilterCount;
  });

  const [dialogOpened, setDialogOpened] = useState<boolean>(false);

  const [category, setCategory] = useState<CategoryInfo | undefined>({
    key: categoryList[categoryIndex].id as CategoryID,
    label: categoryList[categoryIndex].label,
  });

  useEffect(() => {
    setCategory({
      key: categoryList[categoryIndex].id,
      label: categoryList[categoryIndex].label,
    });
  }, [categoryList, categoryIndex]);

  const handleDialogOpen = () => {
    setDialogOpened(true);
  };

  const handleDialogClose = () => {
    setDialogOpened(false);
  };

  const onMobileFilterClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    getOrCreatePulse()?.queueEvent(filtersButtonClickEvent);

    onFiltersButtonClick(e);
  };

  const handleSelectedCategory = (
    selectedCategory: CategoryInfo | undefined
  ) => {
    if (selectedCategory) {
      onCategoryChange(selectedCategory);
      setCategory({
        key: selectedCategory.key,
        label: selectedCategory.label,
      });
    }
  };

  return (
    <>
      <AllCategoriesModal
        show={dialogOpened}
        onClose={handleDialogClose}
        // @ts-expect-error This TS error should go away once we completely deprecate networking library
        setCategoryInput={handleSelectedCategory}
        assetsBase={process.env.NEXT_PUBLIC_ASSETS_BASE_URL}
        categoryId={category?.key}
      />
      <div className={classes['button-category-container']}>
        <Button
          design="outline"
          onClick={handleDialogOpen}
          classes={[classes['button-category']]}
          iconLeft={
            <CategoryIcon className={classes['btn-category-icon-left']} />
          }
          iconRight={
            <ArrowDown className={classes['btn-category-icon-right']} />
          }
        >
          <span
            className={classnames(
              'button-text',
              classes['button-category-text']
            )}
          >
            {category?.label ?? 'Tutte le categorie'}
          </span>
        </Button>
      </div>
      <Button
        design="solid"
        classes={[classes['filter-button']]}
        size="medium"
        onClick={onMobileFilterClick}
      >
        {`Filtri${filterCount > 0 ? ` +${filterCount}` : ''}`}
      </Button>
    </>
  );
};
